import api from './api';
import { Keyword, ApiResponse, PaginatedResponse, RelatedKeyword } from '../types';

interface KeywordFilters {
  page?: number;
  limit?: number;
  search?: string;
  competition?: string;
  is_tracked?: boolean;
  group_id?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  global_only?: string;
}

export interface KeywordGroup {
  id: string;
  name: string;
  description?: string;
  color?: string;
  intent?: 'informational' | 'navigational' | 'transactional' | 'commercial' | 'mixed';
  campaign_id?: string;
  default_bid?: number;
  keywords_count: number;
  average_position?: number;
  total_search_volume?: number;
  average_cpc?: number;
  keywords?: Keyword[];
}

export interface BidStrategy {
  id: string;
  keyword_id: string;
  user_id: string;
  strategy: string;
  target_value: number | null;
  min_bid: number | null;
  max_bid: number | null;
  execution_frequency: string;
  is_active: boolean;
}

export interface BidHistoryEntry {
  id: string;
  keyword_id: string;
  old_bid: number;
  new_bid: number;
  reason: string;
  strategy?: string;
  automation_rule_id?: string;
  created_at: string;
}

export interface KeywordSuggestion {
  keyword: string;
  searchVolume: number;
  difficulty: number;
  cpc: number;
  trend: 'up' | 'down' | 'stable';
  intent: 'informational' | 'transactional' | 'navigational' | 'commercial';
  competition: 'low' | 'medium' | 'high';
  opportunityScore?: number;
}

export interface CompetitorKeyword {
  id: string;
  competitor_id: string;
  keyword: string;
  position: number;
  search_volume: number;
  traffic_share: number;
  cpc: number;
  url?: string;
  userHas?: boolean;
  gap?: boolean;
}

export const keywordService = {
  // Keywords CRUD
  async getKeywords(filters?: KeywordFilters): Promise<PaginatedResponse<Keyword>> {
    const response = await api.get('/keywords', { params: filters });
    return response.data;
  },

  async getKeyword(id: string): Promise<ApiResponse<Keyword>> {
    const response = await api.get(`/keywords/${id}`);
    return response.data;
  },

  async createKeyword(data: Partial<Keyword>): Promise<ApiResponse<Keyword>> {
    const response = await api.post('/keywords', data);
    return response.data;
  },

  async createBulkKeywords(keywords: string[]): Promise<ApiResponse<Keyword[]>> {
    const response = await api.post('/keywords/bulk', { keywords });
    return response.data;
  },

  async updateKeyword(id: string, data: Partial<Keyword>): Promise<ApiResponse<Keyword>> {
    const response = await api.put(`/keywords/${id}`, data);
    return response.data;
  },

  async deleteKeyword(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/keywords/${id}`);
    return response.data;
  },

  async analyzeKeyword(id: string): Promise<ApiResponse<any>> {
    const response = await api.post(`/keywords/${id}/analyze`);
    return response.data;
  },

  // Suggestions
  async getSuggestions(seed: string, language?: string, location?: string): Promise<ApiResponse<RelatedKeyword[]>> {
    const response = await api.get('/keywords/suggestions', {
      params: { seed, language, location }
    });
    return response.data;
  },

  async generateAISuggestions(topic: string, count: number = 20, useAsync: boolean = true): Promise<ApiResponse<KeywordSuggestion[]> & { async?: boolean; jobId?: string }> {
    const response = await api.post('/keywords/suggestions/ai', { topic, count, async: useAsync });
    return response.data;
  },

  // Keyword Groups
  async getGroups(): Promise<ApiResponse<KeywordGroup[]>> {
    const response = await api.get('/keywords/groups');
    return response.data;
  },

  async createGroup(data: Partial<KeywordGroup>): Promise<ApiResponse<KeywordGroup>> {
    const response = await api.post('/keywords/groups', data);
    return response.data;
  },

  async updateGroup(groupId: string, data: Partial<KeywordGroup>): Promise<ApiResponse<KeywordGroup>> {
    const response = await api.put(`/keywords/groups/${groupId}`, data);
    return response.data;
  },

  async deleteGroup(groupId: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/keywords/groups/${groupId}`);
    return response.data;
  },

  async addKeywordsToGroup(groupId: string, keywordIds: string[]): Promise<ApiResponse<void>> {
    const response = await api.post(`/keywords/groups/${groupId}/keywords`, { keywordIds });
    return response.data;
  },

  // Bid Strategy
  async getBidHistory(keywordId: string): Promise<ApiResponse<BidHistoryEntry[]>> {
    const response = await api.get(`/keywords/${keywordId}/bid-history`);
    return response.data;
  },

  async getBidStrategy(keywordId: string): Promise<ApiResponse<BidStrategy | null>> {
    const response = await api.get(`/keywords/${keywordId}/bid-strategy`);
    return response.data;
  },

  async setBidStrategy(keywordId: string, data: {
    strategy: string;
    targetValue?: number | null;
    minBid?: number | null;
    maxBid?: number | null;
    executionFrequency?: string;
  }): Promise<ApiResponse<BidStrategy>> {
    const response = await api.post(`/keywords/${keywordId}/bid-strategy`, data);
    return response.data;
  },

  // Competitor Keywords
  async getCompetitorKeywords(competitorId: string, params?: {
    page?: number;
    limit?: number;
    showGaps?: boolean;
    showShared?: boolean;
  }): Promise<ApiResponse<CompetitorKeyword[]>> {
    const response = await api.get(`/keywords/competitor/${competitorId}`, { params });
    return response.data;
  },

  // Compare Keywords
  async compareKeywords(ids: string[]): Promise<ApiResponse<Keyword[]>> {
    const response = await api.post('/keywords/compare', { ids });
    return response.data;
  },

  // Keywords Explorer - explore a specific keyword
  async exploreKeyword(keyword: string): Promise<ApiResponse<any>> {
    const response = await api.get('/keywords/explore', { params: { keyword } });
    return response.data;
  },

  // Keywords Explorer - explore all keywords of a domain
  async exploreDomainKeywords(domain: string): Promise<ApiResponse<any>> {
    const response = await api.get('/keywords/explore/domain', { params: { domain } });
    return response.data;
  },

  // Rank Tracker - get all tracked keywords with rankings
  async getTrackedKeywords(): Promise<ApiResponse<any[]>> {
    const response = await api.get('/keywords/tracking');
    return response.data;
  },

  // Rank Tracker - get ranking alerts
  async getRankingAlerts(): Promise<ApiResponse<any[]>> {
    const response = await api.get('/keywords/tracking/alerts');
    return response.data;
  },

  // Rank Tracker - refresh all rankings
  async refreshRankings(): Promise<ApiResponse<void>> {
    const response = await api.post('/keywords/tracking/refresh');
    return response.data;
  },

  // Rank Tracker - add keywords to track
  async addTrackedKeywords(keywords: Array<{ keyword: string; device?: string; location?: string }>): Promise<ApiResponse<any[]>> {
    const response = await api.post('/keywords/tracking', { keywords });
    return response.data;
  },

  // Rank Tracker - delete a tracked keyword
  async deleteTrackedKeyword(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/keywords/tracking/${id}`);
    return response.data;
  }
};
